import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { Pencil, Plus, TrashCan } from '@opengov/react-capital-assets';

import { WORK_ORDER_STATUSES, WORK_ORDER_TYPES } from '../../lib/vcm/constants';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetWorkOrdersByCaseId } from '../../mocks/vcm-mock-data';
import type { TcrWorkOrder } from '../../types/vcm';

type StatusColor = 'default' | 'info' | 'warning' | 'success';

const STATUS_COLOR: Record<string, StatusColor> = {
  Unclaimed: 'default',
  Claimed: 'info',
  'In Progress': 'warning',
  Completed: 'success',
};

interface FormValues {
  serviceType: string;
  workOrderType: string;
  address: string;
  requestor: string;
  volOrganization: string;
  status: string;
  workOrderNumber: string;
  notes: string;
}

const EMPTY: FormValues = {
  serviceType: 'Benefits Counseling',
  workOrderType: 'Benefits Counseling',
  address: '',
  requestor: '',
  volOrganization: '',
  status: 'Unclaimed',
  workOrderNumber: '',
  notes: '',
};

/** iSTAT work orders table with add/edit/delete (in-session mock state). */
export function WorkOrdersPanel({ caseId }: { readonly caseId: string }) {
  const [orders, setOrders] = useState<TcrWorkOrder[]>(() => mockGetWorkOrdersByCaseId(caseId));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TcrWorkOrder | null>(null);
  const [values, setValues] = useState<FormValues>(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setValues({ ...EMPTY, ...editing, serviceType: editing.damageLevel ?? EMPTY.serviceType });
    } else {
      setValues(EMPTY);
    }
  }, [open, editing]);

  const openAdd = () => {
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (order: TcrWorkOrder) => {
    setEditing(order);
    setOpen(true);
  };
  const remove = (id: string) => setOrders((prev) => prev.filter((o) => o.id !== id));
  const save = () => {
    const { serviceType, ...rest } = values;
    const mapped = { ...rest, damageLevel: serviceType };
    setOrders((prev) => {
      if (editing) return prev.map((o) => (o.id === editing.id ? { ...editing, ...mapped } : o));
      return [...prev, { id: `wo-${Date.now()}`, caseId, ...mapped }];
    });
    setOpen(false);
  };

  const set = (key: keyof FormValues, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  return (
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Work Orders</Typography>
        <Button size="small" variant="contained" startIcon={<Plus />} onClick={openAdd}>
          Add work order
        </Button>
      </Stack>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Service Type</TableCell>
              <TableCell>Work Order Type</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Requestor</TableCell>
              <TableCell>VOL Organization</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id}>
                <TableCell>{o.damageLevel}</TableCell>
                <TableCell>{o.workOrderType}</TableCell>
                <TableCell>{o.address}</TableCell>
                <TableCell>{o.requestor}</TableCell>
                <TableCell>{o.volOrganization}</TableCell>
                <TableCell>
                  <Chip
                    label={o.status}
                    size="small"
                    variant="outlined"
                    color={STATUS_COLOR[o.status] ?? 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Pencil />}
                      onClick={() => openEdit(o)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      color="error"
                      startIcon={<TrashCan />}
                      onClick={() => remove(o.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: (t) => t.spacing(3) }}>
                  <Typography variant="body2" sx={{ color: (t) => t.palette.text.secondary }}>
                    No work orders recorded.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit work order' : 'Add work order'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Service Type"
                fullWidth
                size="small"
                value={values.serviceType}
                onChange={(e) => set('serviceType', e.target.value)}
              >
                {['Benefits Counseling', 'Housing Assessment', 'Healthcare Navigation', 'Employment Assistance', 'Transportation Support', 'Other'].map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Work Order Type"
                fullWidth
                size="small"
                value={values.workOrderType}
                onChange={(e) => set('workOrderType', e.target.value)}
              >
                {WORK_ORDER_TYPES.map((w) => (
                  <MenuItem key={w} value={w}>
                    {w}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Address"
                fullWidth
                size="small"
                value={values.address}
                onChange={(e) => set('address', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Requestor"
                fullWidth
                size="small"
                value={values.requestor}
                onChange={(e) => set('requestor', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="VOL Organization"
                fullWidth
                size="small"
                value={values.volOrganization}
                onChange={(e) => set('volOrganization', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Status"
                fullWidth
                size="small"
                value={values.status}
                onChange={(e) => set('status', e.target.value)}
              >
                {WORK_ORDER_STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Work Order Number"
                fullWidth
                size="small"
                value={values.workOrderNumber}
                onChange={(e) => set('workOrderNumber', e.target.value)}
              />
            </Grid>
          </Grid>
          <TextField
            label="Notes"
            fullWidth
            multiline
            rows={4}
            value={values.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Additional notes about this work order..."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}>
            {editing ? 'Save changes' : 'Add work order'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
